import { TAB_ITEMS } from '../constants';
import clsx from 'clsx';
import { BlockButton } from '@/components/BlockButton';
import CompaniesProfileMenu from './CompaniesProfileMenu';
import { useRef, useState } from 'react';
import { useMicromodal } from '@/hooks/useMicromodal';
import CompaniesDeleteModal from './CompaniesDeleteModal';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import CompaniesFileUploadModal from './CompaniesFileUploadModal';
import CompanyMergeModal from './CompanyMergeModal';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { AgentApiCompaniesShow } from '@/types/serializers';
import { useSetAtom } from 'jotai';
import { isCompanyProfileEditAtom } from '../atom/companiesAtom';
import CandidatesUserJobProcessScore from '../../candidates/components/CandidatesUserJobProcessScore';
import type { ActiveUser } from '../../types/ActiveUser';
import UsersMenu from '@/features/agent/components/UsersMenu';
import useUpdateCompanyProfile from '../api/useUpdateCompanyProfile';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import CompanyTagEditDialog from './CompanyTagEditDialog';
import { Tag, type TagColor } from '@/components/Tag';
import type { CustomTag } from '../../custom_tags/hooks/useCustomTags';

type Props = {
  companyId: number;
  tab: (typeof TAB_ITEMS)[number]['value'];
  companyName: string;
  presidentName: string;
  address: string;
  matchedCount: number;
  suggestedJobPostsCount: number;
  providedJobPostsCount: number;
  availableProvidedJobPostsCount: number;
  organizationUserIds: number[];
  activeUsers: ActiveUser[];
  currentUserId?: number;
  tags: CustomTag[];
  files: {
    id: number;
    filename: string;
    url: string;
  }[];
  setTab: (val: (typeof TAB_ITEMS)[number]['value']) => void;
  companyRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<AgentApiCompaniesShow, Error>>;
};

export default function CompaniesProfileTab({
  companyId,
  tab,
  setTab,
  companyName,
  address,
  presidentName,
  matchedCount,
  suggestedJobPostsCount,
  providedJobPostsCount,
  availableProvidedJobPostsCount,
  tags,
  organizationUserIds,
  activeUsers,
  currentUserId,
  files,
  companyRefetch,
}: Props) {
  const setIsCompanyProfileEdit = useSetAtom(isCompanyProfileEditAtom);
  const {
    open: profileDeleteOpen,
    close: profileDeleteClose,
    id: profileDeleteId,
    isShow: isProfileDeleteShow,
  } = useMicromodal('profile-delete-modal');
  const [isFileUploadShow, setIsFileUploadShow] = useState(false);
  const fileUploadOpen = () => setIsFileUploadShow(true);
  const fileUploadClose = () => setIsFileUploadShow(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  useOnClickOutside(menuRef, () => setIsMenuOpen(false));

  const { mutateAsync: updateCompany } = useUpdateCompanyProfile();

  const onClickDeleteMenu = () => {
    setIsMenuOpen(false);
    profileDeleteOpen();
  };

  const onClickMergeMenu = () => {
    setIsMenuOpen(false);
    setIsMergeModalOpen(true);
  };

  const onProfileEdit = () => {
    setIsMenuOpen(false);
    setTab('profile');
    setIsCompanyProfileEdit(true);
  };

  const handleSelectAssignees = async (userIds: number[]) => {
    try {
      await updateCompany({ companyId, organization_user_ids: userIds });
      await companyRefetch();
    } catch (e) {
      showErrorToast('担当者の更新に失敗しました');
    }
  };

  return (
    <div className="bg-white shadow-[0px_3px_5px_-3px_#00000040]">
      <div className="flex items-center px-8">
        <div className="flex w-[518px] flex-col gap-4">
          <div className="flex items-center gap-4">
            <p className="text-xl font-bold whitespace-nowrap">
              {companyName.length > 20 ? companyName.slice(0, 20) + '…' : companyName}
            </p>
            <UsersMenu
              activeUsers={activeUsers}
              selectedUserIds={organizationUserIds}
              onSelect={handleSelectAssignees}
              currentUserId={currentUserId}
            />
            <div>
              {files.length === 0 ? (
                <div className="w-[120px]">
                  <BlockButton
                    type="button"
                    text="ファイルを追加"
                    height={32}
                    fontSize="text-xs"
                    icon="/agent/candidates/icon-add.svg"
                    onClick={fileUploadOpen}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  {files.map((f) => {
                    return (
                      <div key={f.id} className="group relative">
                        <button
                          key={f.id}
                          type="button"
                          className="flex size-xl items-center justify-center rounded border border-solid border-border bg-white hover:bg-tag-gray"
                          onClick={() => window.open(f.url, '_blank')}
                        >
                          <img
                            src="/agent/candidates/icon-file.svg"
                            alt=""
                            width={16}
                            height={16}
                          />
                        </button>
                        <div className="absolute top-full left-1/2 z-10 mt-2 hidden w-max -translate-x-1/2 transform rounded bg-black p-2 text-xs text-white group-hover:block">
                          {f.filename}
                          <div className="absolute -top-2 left-1/2 size-0 -translate-x-1/2 border-4 border-solid border-transparent border-b-black"></div>
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className="flex size-xl items-center justify-center rounded bg-primary hover:opacity-60"
                    onClick={fileUploadOpen}
                  >
                    <img src="/agent/candidates/icon-add.svg" alt="" width={16} height={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {tags.map((tag) => (
              <Tag key={tag.id} color={tag.colorHex as TagColor} size="sm">
                {tag.name}
              </Tag>
            ))}
            <CompanyTagEditDialog id={companyId} tags={tags} />
          </div>
          <div className="flex gap-5">
            {address && (
              <div className="flex flex-nowrap items-center gap-2">
                <p className="text-xs font-bold whitespace-nowrap text-placeholder">所在地</p>
                <p className="text-xs whitespace-nowrap">{address}</p>
              </div>
            )}
            {presidentName && (
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold whitespace-nowrap text-placeholder">代表者名</p>
                <p className="text-xs whitespace-nowrap">{presidentName}</p>
              </div>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <CandidatesUserJobProcessScore text="提案数" score={suggestedJobPostsCount} />
          <CandidatesUserJobProcessScore text="マッチング数" score={matchedCount} />
          <CandidatesUserJobProcessScore text="求人数" score={providedJobPostsCount} />
          <div className="relative flex h-xxl items-start justify-end self-start" ref={menuRef}>
            <button
              type="button"
              className={clsx(
                'flex size-[36px] cursor-pointer items-center justify-center',
                isMenuOpen && 'rounded border border-solid border-border bg-tag-gray'
              )}
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen((prev) => !prev);
              }}
            >
              <img src="/agent/candidates/icon-more.svg" width={24} height={24} alt="" />
            </button>
            {isMenuOpen && (
              <div className="absolute top-10 z-50">
                <CompaniesProfileMenu
                  deleteOpen={onClickDeleteMenu}
                  onProfileEdit={onProfileEdit}
                  onMergeOpen={onClickMergeMenu}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-around">
        {TAB_ITEMS.map((item) => (
          <button
            type="button"
            key={item.value}
            className={clsx(
              'h-[44px] w-full cursor-pointer text-sm hover:font-bold hover:text-primary',
              tab === item.value &&
                'border-0 border-b-2 border-solid border-b-primary font-bold text-primary'
            )}
            onClick={() => setTab(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <CompaniesFileUploadModal
        companyId={companyId}
        open={isFileUploadShow}
        onOpenChange={(open) => {
          if (!open) fileUploadClose();
        }}
        files={files}
        companyRefetch={companyRefetch}
      />
      <CompaniesDeleteModal
        companyId={companyId}
        availableProvidedJobPostsCount={availableProvidedJobPostsCount}
        modal={{ id: profileDeleteId, isShow: isProfileDeleteShow }}
        close={profileDeleteClose}
      />
      <CompanyMergeModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        companyId={companyId}
        companyName={companyName}
      />
    </div>
  );
}
